class RRHomeNavigation extends HTMLElement {
  connectedCallback() {
    const header = document.querySelector('.shopify-section--header x-header');
    if (!header || !this.querySelector('a')) return;
    if (this.parentElement !== header) return;
    this.header = header;
    this.primary = header.querySelector('.header__primary-nav');
    this.desktop = matchMedia('(min-width: 1000px)');
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.expanded = window.scrollY <= 80;
    this.queue = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    };
    // Threshold checks do not read layout; apply immediately without an extra frame of delay.
    if (location.pathname === '/pages/story-and-expertise') {
      // Observe the existing hysteresis boundaries instead of synchronizing
      // style/layout through scrollY and activeElement on every scroll event.
      this.scrollMarkers = [40, 80].map(top => {
        const marker = document.createElement('span');
        marker.setAttribute('aria-hidden', 'true');
        marker.style.cssText = `position:absolute;top:${top}px;left:0;width:1px;height:1px;pointer-events:none;opacity:0;`;
        document.body.append(marker);
        return marker;
      });
      this.scrollObserver = new IntersectionObserver(this.queue);
      this.scrollMarkers.forEach(marker => this.scrollObserver.observe(marker));
    } else {
      window.addEventListener('scroll', () => this.update(), { passive: true, signal });
    }
    window.addEventListener('pageshow', this.queue, { signal });
    this.desktop.addEventListener('change', this.queue, { signal });
    header.addEventListener('focusout', this.queue, { signal });
    this.drawerObserver = new MutationObserver(this.queue);
    this.drawerObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    this.update();
    const revealNavigation = () => {
      if (!signal.aborted) header.dataset.rrHomeFontReady = 'true';
    };
    if (document.fonts) document.fonts.load('700 16px "Raiman Rocks"').then(revealNavigation, revealNavigation);
    else revealNavigation();
    this.readyFrame = requestAnimationFrame(() => {
      this.readyFrame = requestAnimationFrame(() => {
        header.dataset.rrHomeReady = 'true';
        this.readyFrame = 0;
      });
    });
  }

  update() {
    const desktop = this.desktop.matches;
    const drawerOpen = document.documentElement.classList.contains('rr-drawer-header-open');
    const focused = this.contains(document.activeElement);
    // Keep compact controls reachable for keyboard focus; a pointer-focused
    // drawer toggle must not pin the header after returning to the top.
    const primaryKeyboardFocus = this.primary?.contains(document.activeElement)
      && document.activeElement.matches(':focus-visible');
    if (!desktop || drawerOpen) this.expanded = false;
    else if (!focused) {
      if (window.scrollY > 80) this.expanded = false;
      else if (window.scrollY <= 40 && !primaryKeyboardFocus) this.expanded = true;
    }
    if (this.lastDesktop === desktop && this.header.dataset.rrHomeExpanded === String(this.expanded)) return;
    this.lastDesktop = desktop;
    if (this.header.dataset.rrHomeExpanded !== String(this.expanded)) {
      this.header.dataset.rrHomeExpanded = String(this.expanded);
    }
    this.inert = !this.expanded;
    this.setAttribute('aria-hidden', String(!this.expanded));
    if (this.primary) this.primary.inert = desktop && this.expanded;
    if (focused && !this.expanded) {
      this.primary?.querySelector('button[aria-controls="sidebar-menu"]')?.focus({ preventScroll: true });
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.drawerObserver?.disconnect();
    this.scrollObserver?.disconnect();
    this.scrollMarkers?.forEach(marker => marker.remove());
    cancelAnimationFrame(this.readyFrame);
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    if (this.header) {
      delete this.header.dataset.rrHomeExpanded;
      delete this.header.dataset.rrHomeReady;
      delete this.header.dataset.rrHomeFontReady;
    }
    if (this.primary) this.primary.inert = false;
  }
}

if (!customElements.get('rr-home-navigation')) customElements.define('rr-home-navigation', RRHomeNavigation);
